import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { achievements, certifications } from "../data/portfolio";

const certColorMap = {
  blue:   { bg: "rgba(96,165,250,0.08)",  border: "rgba(96,165,250,0.2)",  text: "#93c5fd" },
  purple: { bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.2)", text: "#c4b5fd" },
  green:  { bg: "rgba(52,211,153,0.08)",  border: "rgba(52,211,153,0.2)",  text: "#6ee7b7" },
  pink:   { bg: "rgba(244,114,182,0.08)", border: "rgba(244,114,182,0.2)", text: "#f9a8d4" },
};

export default function Achievements() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="achievements" className="relative px-6 md:px-16 lg:px-24 py-32">
      <div className="max-w-6xl mx-auto">
        <div ref={ref}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-label mb-3"
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400">
              05 // Achievements & Certs
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
            className="font-display font-black text-4xl md:text-5xl tracking-tight mb-16"
          >
            Wins &amp; <span className="grad-text">credentials.</span>
          </motion.h2>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
          {/* Achievements */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-mono text-xs tracking-widest uppercase text-white/25 mb-5"
            >
              Achievements
            </motion.p>
            <div className="grid grid-cols-2 gap-4">
              {achievements.map((a, i) => (
                <motion.div
                  key={a.title}
                  initial={{ opacity: 0, y: 24 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.25 + i * 0.1, ease: [0.23, 1, 0.32, 1] }}
                  className="glass-card p-5 text-center group"
                >
                  <span
                    className="text-3xl mb-3 block group-hover:animate-[float_2s_ease-in-out_infinite] transition-all"
                  >
                    {a.icon}
                  </span>
                  <p className="font-display font-bold text-sm text-white mb-1 leading-tight">
                    {a.title}
                  </p>
                  <p className="font-mono text-[11px] text-white/35 leading-snug">{a.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="font-mono text-xs tracking-widest uppercase text-white/25 mb-5"
            >
              Certifications
            </motion.p>
            <div className="space-y-3">
              {certifications.map((cert, i) => {
                const colors = certColorMap[cert.colorClass] || certColorMap.blue;
                return (
                  <motion.div
                    key={cert.name}
                    initial={{ opacity: 0, x: 30 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.3 + i * 0.1, ease: [0.23, 1, 0.32, 1] }}
                    className="glass-card flex items-center gap-4 p-4"
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      {cert.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-white leading-snug truncate">
                        {cert.name}
                      </p>
                      <p className="font-mono text-[11px] tracking-wide mt-0.5" style={{ color: colors.text }}>
                        {cert.issuer}
                      </p>
                    </div>
                    <span
                      className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-mono"
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                      }}
                    >
                      Verified
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
